// Confirmaciones y preguntas de texto propias (Jose 2026-10-11, UX: quitar
// los window.confirm / window.prompt / alert del navegador). Mismo patron
// que Toast: un Provider arriba de la app y dos hooks:
//
//   const confirmar = useConfirm();
//   if (!(await confirmar('¿Eliminar el pedido?'))) return;
//   if (!(await confirmar({ titulo: 'Eliminar pedido', mensaje, ok: 'Sí, eliminar', peligro: true }))) return;
//
//   const pedirTexto = usePrompt();
//   const nombre = await pedirTexto({ titulo: 'Renombrar', label: 'Nuevo nombre', valorInicial: 'Sticker' });
//   if (nombre == null) return;   // cancelo
//
// Devuelven una Promise como los dialogos nativos, asi el flujo del caller
// no cambia (solo se vuelve async). Enter confirma, Escape cancela. El boton
// de OK sale rojo si `peligro` es true o si el texto habla de borrar /
// eliminar / irreversible.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import { FONT_FAMILY } from '../constants/font';

const Ctx = createContext(null);
const RE_PELIGRO = /elimin|borrar|borra |irreversible|no se puede deshacer|destructiv|purga|desactiv|definitiv/i;

function normalizar(opts) {
  const o = typeof opts === 'string' ? { mensaje: opts } : (opts || {});
  const mensaje = String(o.mensaje || '');
  return {
    titulo: o.titulo || (o.tipo === 'prompt' ? 'Escribe un dato' : 'Confirmación'),
    mensaje,
    ok: o.ok || (o.tipo === 'prompt' ? 'Guardar' : 'Confirmar'),
    cancelar: o.cancelar || 'Cancelar',
    peligro: o.peligro != null ? !!o.peligro : RE_PELIGRO.test(mensaje + ' ' + (o.titulo || '') + ' ' + (o.ok || '')),
    label: o.label || '',
    valorInicial: o.valorInicial != null ? String(o.valorInicial) : '',
    placeholder: o.placeholder || '',
    multilinea: !!o.multilinea,
    validar: typeof o.validar === 'function' ? o.validar : null,  // (texto) => mensaje de error | ''
    obligatorio: o.obligatorio !== false,
  };
}

export function ConfirmProvider({ children }) {
  const [estado, setEstado] = useState(null);
  const [valor, setValor] = useState('');
  const [error, setError] = useState('');
  const resolverRef = useRef(null);

  const cerrar = useCallback((resultado) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setEstado(null);
    setError('');
    if (r) r(resultado);
  }, []);

  const abrir = useCallback((tipo, opts) => new Promise(resolve => {
    // Si habia uno abierto, se resuelve como cancelado.
    if (resolverRef.current) resolverRef.current(tipo === 'prompt' ? null : false);
    resolverRef.current = resolve;
    const n = normalizar({ ...(typeof opts === 'string' ? { mensaje: opts } : opts), tipo });
    setValor(n.valorInicial);
    setError('');
    setEstado({ tipo, ...n });
  }), []);

  const confirmar = useCallback((opts) => abrir('confirm', opts), [abrir]);
  const pedirTexto = useCallback((opts) => abrir('prompt', opts), [abrir]);

  const aceptar = useCallback(() => {
    if (!estado) return;
    if (estado.tipo === 'confirm') { cerrar(true); return; }
    const texto = valor.trim();
    if (estado.obligatorio && !texto) { setError('Escribe algo o cancela.'); return; }
    const msg = estado.validar ? estado.validar(texto) : '';
    if (msg) { setError(msg); return; }
    cerrar(texto);
  }, [estado, valor, cerrar]);

  const cancelar = useCallback(() => {
    if (!estado) return;
    cerrar(estado.tipo === 'prompt' ? null : false);
  }, [estado, cerrar]);

  useEffect(() => {
    if (!estado) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancelar(); }
      else if (e.key === 'Enter' && !(estado.tipo === 'prompt' && estado.multilinea)) { e.preventDefault(); aceptar(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [estado, aceptar, cancelar]);

  return (
    <Ctx.Provider value={{ confirmar, pedirTexto }}>
      {children}
      <Modal open={!!estado} onClose={cancelar} title={estado ? estado.titulo : ''} width={440}>
        {estado && (
          <div style={S.cuerpo} role={estado.tipo === 'confirm' ? 'alertdialog' : 'dialog'}>
            {estado.mensaje && <div style={S.mensaje}>{estado.mensaje}</div>}
            {estado.tipo === 'prompt' && (
              <label style={S.label}>
                {estado.label && <span>{estado.label}</span>}
                {estado.multilinea ? (
                  <textarea autoFocus rows={3} value={valor} onChange={e => setValor(e.target.value)} placeholder={estado.placeholder} style={S.input} />
                ) : (
                  <input autoFocus type="text" value={valor} onChange={e => setValor(e.target.value)} placeholder={estado.placeholder} style={S.input} />
                )}
              </label>
            )}
            {error && <div style={S.error}>{error}</div>}
            <div style={S.acciones}>
              <button type="button" onClick={cancelar} style={S.btn}>{estado.cancelar}</button>
              <button
                type="button"
                onClick={aceptar}
                autoFocus={estado.tipo === 'confirm'}
                style={{ ...S.btn, ...(estado.peligro ? S.btnPeligro : S.btnOk) }}
              >
                {estado.ok}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Ctx.Provider>
  );
}

const SIN_PROVIDER = 'useConfirm/usePrompt requieren <ConfirmProvider> arriba de la app';

/** confirmar(mensaje | { titulo, mensaje, ok, cancelar, peligro }) -> Promise<boolean> */
export function useConfirm() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error(SIN_PROVIDER);
  return ctx.confirmar;
}

/** pedirTexto({ titulo, mensaje, label, valorInicial, placeholder, multilinea, validar, obligatorio }) -> Promise<string|null> */
export function usePrompt() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error(SIN_PROVIDER);
  return ctx.pedirTexto;
}

const S = {
  cuerpo: { display: 'flex', flexDirection: 'column', gap: 12, fontFamily: FONT_FAMILY },
  mensaje: { fontSize: 14, color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-line' },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600, color: '#374151' },
  input: { width: '100%', padding: '9px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 14, fontFamily: FONT_FAMILY, boxSizing: 'border-box' },
  error: { fontSize: 12, color: '#991B1B', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '6px 10px' },
  acciones: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
  btn: { padding: '9px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_FAMILY },
  btnOk: { background: '#1A1A1A', borderColor: '#1A1A1A', color: '#fff' },
  btnPeligro: { background: '#DC2626', borderColor: '#DC2626', color: '#fff' },
};
